import { AbstractControl, ValidationErrors } from '@angular/forms';

type IOpcaoDocumento = 'cpf' | 'cnpj';

export function formatarData(data: Date | string): string {
  if (!data) {
    return '---';
  }
  const dataFormatada = new Date(data).toLocaleString('pt-BR');
  return dataFormatada.toString().replace(',', ' ');
}

export function formatarDataSemHorario(data: Date | string): string {
  if (!data) {
    return '---';
  }
  return new Date(data).toLocaleDateString('pt-BR');
}

/** Normaliza valor de filtro (date ou datetime-local) para parâmetro de API. */
export function formatarFiltroDataApi(
  value: string | Date | null | undefined
): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  const raw = String(value).trim();
  if (!raw) {
    return undefined;
  }

  const datePart = raw.includes('T') ? raw.split('T')[0] : raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return raw;
  }

  return datePart;
}

export function formatarPlaca(placa: string): string {
  if (!placa) {
    return '---';
  }
  const placaUpperCase = placa.toUpperCase();
  return placaUpperCase
    .split('|')
    .map((placa) => {
      placa = placa.trim();
      if (placa.length === 7) {
        return `${placa.substring(0, 3)}-${placa.substring(3, 7)}`;
      }
      return placa;
    })
    .join(' | ');
}

export function trimEmailValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (control.value && typeof control.value === 'string') {
    const trimmed = control.value.trim();

    if (trimmed !== control.value) {
      control.setValue(trimmed, { emitEvent: false });
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailPattern.test(trimmed)) {
      return { email: true };
    }
  }

  return null;
}

export function isPlacaValida(value: string): boolean {
  let hasLetter = false;
  let hasNumber = false;

  for (let i = 0; i < value.length; i++) {
    if (/^[A-Za-z]$/.test(value[i])) {
      hasLetter = true;
    } else if (/^[0-9]$/.test(value[i])) {
      hasNumber = true;
    }

    if (hasLetter && hasNumber) {
      return true;
    }
  }

  return false;
}

export function isMultiplasPlacasValidas(...values: string[]): boolean {
  return values.every((value) => {
    let hasLetter = false;
    let hasNumber = false;

    for (let i = 0; i < value.length; i++) {
      if (/^[A-Za-z]$/.test(value[i])) {
        hasLetter = true;
      } else if (/^[0-9]$/.test(value[i])) {
        hasNumber = true;
      }

      if (hasLetter && hasNumber) {
        return true;
      }
    }

    return false;
  });
}

export function formatarCpfCnpj(value: string, type: IOpcaoDocumento) {
  if (type == 'cpf') {
    value = value.replace(/\D/g, '');

    if (value.length <= 11) {
      value = value.padStart(11, '0');
      return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
  }

  if (type == 'cnpj') {
    value = value.replace(/\D/g, '');

    if (value.length <= 14) {
      value = value.padStart(14, '0');
      return value.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        '$1.$2.$3/$4-$5'
      );
    }
  }

  return value;
}

export function formatarTelefone(value: string) {
  value = value.replace(/\D/g, '');

  if (value.length === 11) {
    return value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (value.length === 10) {
    return value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }

  return value;
}

export function validarIsNullOrEmpty(value: any): boolean {
  const invalid = ['', null, undefined, NaN, '---', 0];

  return invalid.includes(value);
}

export function tratarIsNullOrEmpty(value: any): any {
  const invalid = ['', null, undefined, NaN, '---', 0];

  return invalid.includes(value) ? null : value;
}

export function tratarIsNullOrEmptyString(value: any): null | string {
  const invalid = ['', null, undefined, NaN, '---', 0];

  return invalid.includes(value) ? null : String(value).toUpperCase();
}

export function removeHyphen(value: string): string {
  return value.replace(/-/g, '');
}

export function formateMoney(value: string): string {
  if (!value) {
    return 'Não informado';
  }
  const dinheiroFormatado = 'R$ ' + value;
  return dinheiroFormatado.toString().replace('.', ',');
}

export function validarCPF(control: AbstractControl) {
  const cpf = control.value?.replace(/\D/g, '');

  if (cpf?.length == 0) return null;

  if (!cpf || cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
    return { cpfInvalido: true };
  }

  let soma = 0,
    resto;
  for (let i = 1; i <= 9; i++) soma += parseInt(cpf[i - 1]) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf[9])) return { cpfInvalido: true };

  soma = 0;
  for (let i = 1; i <= 10; i++) soma += parseInt(cpf[i - 1]) * (12 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf[10])) return { cpfInvalido: true };

  return null;
}

function validarCNPJ(cnpj: string): boolean {
  if (/^(\d)\1+$/.test(cnpj)) return false;

  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  let digitos = cnpj.substring(tamanho);
  let soma = 0,
    pos = tamanho - 7;

  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros[tamanho - i]) * pos--;
    if (pos < 2) pos = 9;
  }

  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== parseInt(digitos[0])) return false;

  tamanho += 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;

  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros[tamanho - i]) * pos--;
    if (pos < 2) pos = 9;
  }

  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  return resultado === parseInt(digitos[1]);
}

export function validarCpfCnpj(
  control: AbstractControl
): ValidationErrors | null {
  const valor = control.value?.replace(/\D/g, '');
  if (!valor) return null;

  const validarCpfTexto = (cpf: string): boolean => {
    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

    let soma = 0;
    let resto: number;

    for (let i = 1; i <= 9; i++) {
      soma += parseInt(cpf[i - 1]) * (11 - i);
    }

    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf[9])) return false;

    soma = 0;
    for (let i = 1; i <= 10; i++) {
      soma += parseInt(cpf[i - 1]) * (12 - i);
    }

    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;

    return resto === parseInt(cpf[10]);
  };

  if (valor.length === 11) {
    return validarCpfTexto(valor) ? null : { cpfInvalido: true };
  } else if (valor.length === 14) {
    return validarCNPJ(valor) ? null : { cnpjInvalido: true };
  }

  return { documentoInvalido: true };
}
